import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authService } from '../services/auth.service';

export interface User {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  profileCompleted: boolean;
  hasProfile: boolean;
  marketingConsent?: boolean;
  marketingConsentAsked?: boolean;
  createdAt: Date;
  tier?: string;
  subscriptionStatus?: string | null;
  subscriptionPeriodEnd?: string | null;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  accessToken: string | null;
  refreshToken: string | null;
  hasHydrated: boolean;
  mode: 'local' | 'cloud';
}

interface AuthActions {
  login: (user: User, tokens: { accessToken: string; refreshToken: string }) => void;
  logout: () => void;
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  updateUser: (updates: Partial<User>) => void;
  setHasHydrated: (hydrated: boolean) => void;
  setMode: (mode: 'local' | 'cloud') => void;
  refreshUser: () => Promise<void>;
}

export type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      // State
      user: null,
      isAuthenticated: false,
      isLoading: false,
      accessToken: null,
      refreshToken: null,
      hasHydrated: false,
      mode: 'cloud',

      // Actions
      login: (user, tokens) => {
        localStorage.setItem('accessToken', tokens.accessToken);
        localStorage.setItem('refreshToken', tokens.refreshToken);
        set({
          user,
          isAuthenticated: true,
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          mode: 'cloud', // Always cloud when authenticated
        });
      },

      logout: () => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        set({
          user: null,
          isAuthenticated: false,
          accessToken: null,
          refreshToken: null,
          mode: 'cloud',
        });
      },

      setUser: (user) => {
        set({ user, isAuthenticated: !!user });
      },

      setLoading: (loading) => {
        set({ isLoading: loading });
      },

      updateUser: (updates) => {
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : null,
        }));
      },

      setHasHydrated: (hydrated) => set({ hasHydrated: hydrated }),

      setMode: (mode) => set({ mode }),

      refreshUser: async () => {
        try {
          const user = await authService.getCurrentUser();
          if (user) {
            // API returns createdAt as an ISO string; the store's User type
            // uses Date, so convert explicitly instead of unsafely casting.
            set({ user: { ...user, createdAt: new Date(user.createdAt) } });
          }
        } catch (error) {
          console.error('[AuthStore] Failed to refresh user:', error);
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        mode: state.mode,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    }
  )
);
